'use client';

import { Button } from '@packages/shared/src/ui/Button/Button';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import { defineMessages, useIntl } from 'react-intl';
import { MiniDVLayer } from '@packages/modules/src/MiniDVLayer/MiniDVLayer.client';
import { AddToBasketButtonTailwind } from '@packages/modules/src/AddToBasketButton/AddToBasketButtonTailwind.client';
import type { ButtonHTMLAttributes } from 'react';
import { useState } from 'react';
import { WishlistFragmentFragmentDoc } from '@packages/gql/generated/shopping/WishlistFragmentFragmentDoc';
import {
  type FragmentType,
  getFragmentData,
  makeFragmentData,
} from '@packages/gql/generated/shopping';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { BasketActionsFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketActionsFragmentFragmentDoc';
import { AddToBasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/AddToBasketFragmentFragmentDoc';
import type {
  GlycerinCartProduct,
  GlycerinWishListProduct,
} from '@packages/tracking/src/types/types';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { buttonStylingTailwind } from './Actions.styles';
import { GraphqlDocuments } from './mappedModeToDocument';

export interface BasketActionProps {
  data: FragmentType<typeof BasketActionsFragmentFragmentDoc>;
  glycerinProduct: GlycerinCartProduct | GlycerinWishListProduct;
  className?: string;
  galleryView?: boolean;
}

const messages = defineMessages({
  buttonTextShort: {
    id: 'BasketAction.ButtonText',
    defaultMessage: 'Warenkorb',
    description: 'this will be displayed on the button',
  },
  buttonTextLong: {
    id: 'BasketAction.ButtonText.Long',
    defaultMessage: 'in den Warenkorb',
    description: 'this will be displayed on the button',
  },
  successMessage: {
    id: 'BasketAction.SuccessMessage',
    defaultMessage: 'Der Artikel wurde erfolgreich in Ihren Warenkorb gelegt',
  },
  ariaLabel: {
    id: 'BasketAction.AreaLabel',
    defaultMessage: 'In den Warenkorb',
  },
});
/* GraphQL */ `
  fragment BasketActionsFragment on WishlistLineItem {
    sku
    akl
    isSoldOut
    price {
      currency
      saving {
        value
      }
      value
      valueOld
    }
    unspecified
  }
`;
export const BasketAction: React.FC<
  BasketActionProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>
> = ({ data, className, galleryView, glycerinProduct, onClick, ...props }) => {
  const [open, setOpen] = useState(false);
  const { locale } = useI18n();
  const { formatMessage } = useIntl();
  const { addNotification } = useNotification();
  const isDesktop = useMediaType();
  const { akl, isSoldOut, price, sku, unspecified } = getFragmentData(
    BasketActionsFragmentFragmentDoc,
    data,
  );

  const [wishListState, removeFromWishlist] = useAuthMutation(GraphqlDocuments.wishlist);
  const handleRemoveFromWishlist = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    setOpen(false);
    // grab original lineItemSku make call against backend to remove from wishlist
    // this is a callback function that will only occur when the user clicks to add to basket
    if (wishListState.fetching) return;
    removeFromWishlist({ locale, sku })
      .then((result) => {
        if (result.error) throw new Error('error');
        const removeFromWishlistResult = result.data?.removeFromWishlist;
        const wishlist = getFragmentData(WishlistFragmentFragmentDoc, removeFromWishlistResult);
        if (wishlist) {
          addNotification({
            severity: 'success',
            message: formatMessage(messages.successMessage),
            duration: 5000,
            onClose: () => {},
          });
        }
      })
      .catch(() => {
        addNotification({
          severity: 'error',
          message: 'Fehler beim Entfernen des Artikels',
          duration: 5000,
          onClose: () => {},
        });
      });
  };
  const handleOpenDialog = () => {
    setOpen(true);
    // this will fire without having to wait for the response from the backend, if you want to wait for the response use handleRemoveFromWishlist instead
    //   if (!state.fetching && lineItemId && isGlycerinCartProduct(glycerinProduct)) {
    //         dispatchGlycerin<GTMEventGlycerinRemoveFromCart>({
    //           event: 'RemoveFromCart',
    //           RemoveFromCartData: {
    //             removedProducts: [glycerinProduct],
    //           },
    //         });
    //       }
    //     });
    //   }
  };

  if (isSoldOut) return null;

  // Prepare common data for AddToBasketButtonTailwind
  const basketData =
    !unspecified && price !== null
      ? makeFragmentData(
          {
            akl,
            sku,
            availability: {
              isOrderable: !isSoldOut,
              state: !unspecified ? 'AVAILABLE' : 'UNSELECTED',
            },
            price: {
              currency:
                price.currency === 'EUR' || price.currency === 'CHF' ? price.currency : 'EUR',
              saving: price?.saving?.value !== undefined ? { value: price.saving.value } : null,
              value: price.value,
              valueOld: price.valueOld,
            },
            marketcode: null,
          },
          AddToBasketFragmentFragmentDoc,
        )
      : null;

  // Gallery view: icon-only variant
  if (galleryView) {
    return basketData ? (
      <AddToBasketButtonTailwind
        data={basketData}
        data-testid="basket-action"
        color="default"
        onClose={handleRemoveFromWishlist}
        iconClassName="size-4 md:size-3 text-text-dark"
        className={`${buttonStylingTailwind} ${className ?? ''} flex-row-reverse`}
        variant="icon-only"
        customButtonText={null}
        hideEndIcon={false}
        aria-label={formatMessage(messages.ariaLabel)}
      />
    ) : (
      <>
        <Button
          className={`${buttonStylingTailwind} ${className ?? ''}`}
          onClick={handleOpenDialog}
          variant="icon-only"
          color="default"
          data-testid="basket-action"
          size="base"
          aria-label={formatMessage(messages.ariaLabel)}
          {...props}
        >
          <div className="icon-emp-cart size-4 md:size-3" />
        </Button>
        <MiniDVLayer
          sku={sku}
          akl={akl}
          open={open}
          quantity={1}
          onClick={handleRemoveFromWishlist}
          onClose={() => setOpen(false)}
        />
      </>
    );
  }

  return basketData ? (
    <AddToBasketButtonTailwind
      data={basketData}
      data-testid="basket-action"
      color="default"
      onClose={handleRemoveFromWishlist}
      iconClassName="size-4 md:size-3 text-text-dark"
      className={cn(buttonStylingTailwind, className, 'flex-row-reverse')}
      variant={isDesktop ? 'icon-flat' : 'color-button'}
      customButtonText={isDesktop ? messages.buttonTextShort : messages.buttonTextLong}
      hideEndIcon={!isDesktop}
      aria-label={formatMessage(messages.ariaLabel)}
    />
  ) : (
    <>
      <Button
        className={cn(buttonStylingTailwind, className)}
        onClick={handleOpenDialog}
        variant={isDesktop ? 'icon-flat' : 'color-button'}
        color="default"
        data-testid="basket-action"
        size="base"
        aria-label={undefined}
        {...props}
      >
        {isDesktop && <div className="icon-emp-cart size-4 md:size-3" />}
        {formatMessage(isDesktop ? messages.buttonTextShort : messages.buttonTextLong)}
      </Button>

      <MiniDVLayer
        sku={sku}
        akl={akl}
        open={open}
        quantity={1}
        onClick={handleRemoveFromWishlist}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
